import {
    isEmbeddedSidebar,
    isReportMode,
    mcpToolsAvailable,
    modelSelectorAvailable
} from '@/lib/embedded-sidebar';
import { hasMessage } from '@/lib/message';
import { cn } from '@/lib/utils';
import { Cpu, Database, Podcast, Settings, X } from 'lucide-react';
import {
    MutableRefObject,
    useCallback,
    useEffect,
    useRef,
    useState
} from 'react';
import { useParams } from 'react-router-dom';
import { AnimatePresence, motion } from 'framer-motion';
import { useRecoilState, useRecoilValue, useSetRecoilState } from 'recoil';
import { v4 as uuidv4 } from 'uuid';

import {
    FileSpec,
    IStep,
    transcriptionTextState,
    useAudio,
    useAuth,
    useChatData,
    useChatInteract,
    useChatMessages,
    useChatSession,
    useConfig
} from '@chainlit/react-client';

import { Button } from '@/components/ui/button';
import { useTranslation } from 'components/i18n/Translator';

import { withReferences } from '@/lib/dashboardReferences';
import { spring } from '@/lib/springs';
import { chatSettingsOpenState } from '@/state/project';
import {
    IAttachment,
    attachmentsState,
    dashboardReferencesState,
    pendingPromptState,
    queuedPromptState,
    composerModeState,
    debugModeState,
    persistentCommandState,
    selectedModelState,
    selectedSourcesState,
    selectedSubAgentsState
} from 'state/chat';

import AgentDebugCommandButton from './AgentDebugCommandButton';
import { AgentDebugView, findLocalCommand } from './localCommands';
import { Attachments } from './Attachments';
import { DashboardReferences } from './DashboardReferences';
import CommandButtons from './CommandButtons';
import CommandButton from './CommandPopoverButton';
import { getConfigTypeIcon } from './CustomChatConfiguration/DMindex';
import { useSourcesData } from './CustomChatConfiguration/DMuseSourcesData';
import ModelSelector from './DMModelSelector';
import AgentDebugDialog from './DMMoreConfigBtnGroup/AgentDebugDialog';
import MoreConfigBtn from './DMMoreConfigBtnGroup/DMindex';
import { MultiAgentSelector } from './DMMultiAgentSelector';
import DMSourcesPopover from './DMSourcesPopover';
import { SubmitBtn } from './DMSubmitBtn';
import DMSuggestedPrompts from './DMSuggestedPrompts/DMSuggestedPrompts';
import { useDMSuggestedPromptsData } from './DMSuggestedPrompts/useDMSuggestedPromptsData';
// import SubmitButton from './SubmitButton';
// import UploadButton from './UploadButton';
import { VoiceBtn } from './DMVoiceBtn';
import Input, { InputMethods } from './Input';
import McpButton from './Mcp';
import SubmitButton from './SubmitButton';
import UploadButton from './UploadButton';
import VoiceButton from './VoiceButton';
// import PodcastGenerateButton from '../PodcastGenerateButton';
import { waitForPendingAgentSelection } from './agentSelectionGate';
import AskUserQuestionsDock from './AskUserQuestionsDock';
import NotificationPromptDock from './NotificationPromptDock';
import SessionBudgetDock from './SessionBudgetDock';
import ToolApprovalDock from './ToolApprovalDock';
import { useResponseNotifications } from './useResponseNotifications';

interface Props {
    fileSpec: FileSpec;
    onFileUpload: (payload: File[]) => void;
    onFileUploadError: (error: string) => void;
    autoScrollRef: MutableRefObject<boolean>;
    onBeforeSubmit?: (
        message: string,
        attachments: IAttachment[],
        commandId?: string
    ) => void | Promise<void> | Promise<string | null>;
    hideSuggestedPrompts?: boolean;
    pinnedAgentName?: string;
    disabled?: boolean;
}

export default function MessageComposer({
    fileSpec,
    onFileUpload,
    onFileUploadError,
    autoScrollRef,
    onBeforeSubmit,
    hideSuggestedPrompts,
    pinnedAgentName,
    disabled: forceDisabled
}: Props) {
    const inputRef = useRef<InputMethods>(null);
    const [value, setValue] = useState('');
    const [selectedCommand, setSelectedCommand] = useRecoilState(
        persistentCommandState
    );
    const setChatSettingsOpen = useSetRecoilState(chatSettingsOpenState);
    const [attachments, setAttachments] = useRecoilState(attachmentsState);
    const [dashboardReferences, setDashboardReferences] = useRecoilState(
        dashboardReferencesState
    );
    const [pendingPrompt, setPendingPrompt] =
        useRecoilState(pendingPromptState);
    const [queuedPrompt, setQueuedPrompt] = useRecoilState(queuedPromptState);
    const { t } = useTranslation();
    const [selectedSources, setSelectedSources] =
        useRecoilState(selectedSourcesState);
    const selectedModel = useRecoilState(selectedModelState)[0];
    const composerMode = useRecoilState(composerModeState)[0];

    // Sources data for the popover quick-toggle
    const {
        dataSources,
        selectedSources: sourcesFromHook,
        isLoading: loadingSources,
        error: sourcesError,
        isSaving: savingSources,
        toggleSource
    } = useSourcesData();

    // State for controlling CustomChatConfiguration
    const [configOpen, setConfigOpen] = useState(false);
    const [configTab, setConfigTab] = useState('sources');
    // Agent debug modal — triggered by typing the `/agent_debug` command, or via
    // the debug-mode-only slash-command launcher (AgentDebugCommandButton).
    const [agentDebugOpen, setAgentDebugOpen] = useState(false);
    const [agentDebugToken, setAgentDebugToken] = useState(0);
    const [agentDebugView, setAgentDebugView] = useState<AgentDebugView>('full');
    const debugMode = useRecoilValue(debugModeState);

    const openAgentDebug = useCallback((view: AgentDebugView) => {
        setAgentDebugView(view);
        setAgentDebugToken((t) => t + 1);
        setAgentDebugOpen(true);
    }, []);

    // Animation state for staggered source tags
    const [mounted, setMounted] = useState(false);
    useEffect(() => {
        const timer = setTimeout(() => setMounted(true), 600);
        return () => clearTimeout(timer);
    }, []);

    // Handlers for opening configuration with specific tabs
    const openSourcesConfig = useCallback(() => {
        setConfigTab('sources');
        setConfigOpen(true);
    }, []);

    const openModelsConfig = useCallback(() => {
        setConfigTab('models');
        setConfigOpen(true);
    }, []);

    const handleRemoveSource = useCallback(
        (sourceName: string, e: React.MouseEvent) => {
            e.stopPropagation();
            setSelectedSources((prev) =>
                prev.filter((source) => source.name !== sourceName)
            );
        },
        [setSelectedSources]
    );

    const handleConfigClose = useCallback(() => {
        // Focus the input when the configuration dialog closes
        inputRef.current?.focus();
    }, []);

    const handleInputFocus = useCallback(() => {
        // Focus the input after button actions
        inputRef.current?.focus();
    }, []);

    const handleSelectSuggestedPrompt = useCallback((promptText: string) => {
        inputRef.current?.setValue(promptText);
        inputRef.current?.focus();
    }, []);

    const { prompts: suggestedPrompts } = useDMSuggestedPromptsData();

    const { user } = useAuth();
    const { sendMessage, replyMessage } = useChatInteract();
    const {
        askUser,
        elements,
        chatSettingsInputs,
        disabled: _disabled,
        connected,
        loading: isTaskRunning
    } = useChatData();
    const { audioConnection } = useAudio();
    const { config } = useConfig();

    const pendingQuestions =
        askUser?.spec.type === 'element'
            ? elements.find(
                  (element) =>
                      element.type === 'custom' &&
                      element.name === 'AskUserQuestions' &&
                      element.forId === askUser.spec.step_id
              )
            : undefined;
    const pendingToolApproval =
        askUser?.spec.type === 'element'
            ? elements.find(
                  (element) =>
                      element.type === 'custom' &&
                      element.name === 'ToolApproval' &&
                      element.forId === askUser.spec.step_id
              )
            : undefined;
    const { session } = useChatSession();
    const {
        threadId: currentThreadId,
        messages,
        firstInteraction
    } = useChatMessages();
    const { id: urlThreadId } = useParams<{ id?: string }>();
    const notifications = useResponseNotifications({
        isTaskRunning,
        pendingQuestionId:
            pendingQuestions || pendingToolApproval
                ? askUser?.spec.step_id
                : undefined
    });
    const transcriptionText = useRecoilValue(transcriptionTextState);

    const disabled =
        _disabled ||
        !!forceDisabled ||
        !!attachments.find((a) => !a.uploaded);

    const hasContent =
        !!value.trim() || !!selectedCommand || attachments.length > 0;
    const isVoiceActive =
        audioConnection === 'on' || audioConnection === 'connecting';
    const isTaskStoppable = isTaskRunning && firstInteraction;
    const showVoiceButton =
        !isReportMode &&
        !!config?.features.audio.enabled &&
        (isVoiceActive || (!hasContent && !isTaskStoppable));

    // Track if we've already processed a pending message for this thread
    const processedPendingRef = useRef<string | null>(null);

    const onPaste = useCallback(
        (event: ClipboardEvent) => {
            if (event.clipboardData && event.clipboardData.items) {
                const items = Array.from(event.clipboardData.items);

                // If no text data, check for files (e.g., images)
                items.forEach((item) => {
                    if (item.kind === 'file') {
                        const file = item.getAsFile();
                        if (file) {
                            onFileUpload([file]);
                        }
                    }
                });
            }
        },
        [onFileUpload]
    );

    const onSubmit = useCallback(
        async (
            msg: string,
            attachments?: IAttachment[],
            selectedCommand?: string
        ) => {
            const hasUploadedAttachments = !!attachments?.some(
                (attachment) => !!attachment.serverId
            );
            const normalizedMessage =
                msg.trim() === '' && hasUploadedAttachments
                    ? 'Uploaded files'
                    : msg;

            const message: IStep = {
                threadId: '',
                command: selectedCommand,
                id: uuidv4(),
                name: user?.identifier || 'User',
                type: 'user_message',
                output: normalizedMessage,
                createdAt: new Date().toISOString(),
                metadata: { location: window.location.href }
            };

            const fileReferences = attachments
                ?.filter((a) => !!a.serverId)
                .map((a) => ({ id: a.serverId! }));

            if (autoScrollRef) {
                autoScrollRef.current = true;
            }
            // Block on any agent-selection persist that's still in flight or
            // queued (picking an agent right before hitting Send) — without
            // this, the message can reach the backend before the selection
            // lands, and it silently runs on whatever agent was selected
            // BEFORE this one. See agentSelectionGate.ts.
            await waitForPendingAgentSelection();
            sendMessage(message, fileReferences);
        },
        [user, sendMessage, autoScrollRef]
    );

    const onReply = useCallback(
        async (msg: string) => {
            const message: IStep = {
                threadId: '',
                id: uuidv4(),
                name: user?.identifier || 'User',
                type: 'user_message',
                output: msg,
                createdAt: new Date().toISOString(),
                metadata: { location: window.location.href }
            };

            replyMessage(message);
            if (autoScrollRef) {
                autoScrollRef.current = true;
            }
        },
        [user, replyMessage, autoScrollRef]
    );

    const submit = useCallback(async () => {
        if (
            disabled ||
            pendingQuestions ||
            pendingToolApproval ||
            (value.trim() === '' &&
                attachments.length === 0 &&
                !selectedCommand)
        ) {
            return;
        }

        const typedLocalCommand = findLocalCommand(value);
        if (
            typedLocalCommand &&
            (debugMode || typedLocalCommand.view === 'full')
        ) {
            setValue('');
            inputRef.current?.reset();
            openAgentDebug(typedLocalCommand.view);
            return;
        }

        // Call onBeforeSubmit if provided (e.g., to fork a shared thread)
        let newThreadId: string | null = null;
        if (onBeforeSubmit) {
            const result = await onBeforeSubmit(
                value,
                attachments,
                selectedCommand?.id
            );
            if (typeof result === 'string') {
                newThreadId = result;
            }
        }

        // If forking, clear input and return - message will be sent after navigation
        if (newThreadId) {
            setAttachments([]);
            setValue('');
            inputRef.current?.reset();
            return;
        }

        // Multi-agent mode: the sub-agent roster is already persisted to the
        // backend the moment it was toggled in MultiAgentSelector (mirroring
        // agent+MCP selection) — orchestrator activation itself happens
        // server-side, inline inside foundation()'s "Preparation of agent"
        // step, when the message below is sent. Nothing to do here.

        if (askUser) {
            onReply(value);
        } else {
            onSubmit(
                withReferences(value, dashboardReferences),
                attachments,
                selectedCommand?.id
            );
        }

        // Dispatch file-uploaded event if there were attachments
        // This triggers a refresh of thread data to show newly uploaded files
        if (attachments.length > 0) {
            // Dispatch loading event immediately to show spinner
            window.dispatchEvent(
                new CustomEvent('file-uploading', {
                    detail: { fileCount: attachments.length }
                })
            );

            // Dispatch refresh event after delay to allow the server to process the files
            setTimeout(() => {
                window.dispatchEvent(
                    new CustomEvent('file-uploaded', {
                        detail: { fileCount: attachments.length }
                    })
                );
            }, 1000);
        }

        setAttachments([]);
        setDashboardReferences([]);
        setValue(''); // Clear the value state
        inputRef.current?.reset();
    }, [
        value,
        disabled,
        askUser,
        pendingQuestions,
        pendingToolApproval,
        attachments,
        dashboardReferences,
        setDashboardReferences,
        selectedCommand,
        debugMode,
        openAgentDebug,
        setAttachments,
        onSubmit,
        onReply,
        onBeforeSubmit
    ]);

    // Auto-send the first message of a forked shared thread once we've navigated
    // to the new thread and its session has fully resumed.
    //
    // The server emits `first_interaction` (which sets currentThreadId) BEFORE
    // it emits `resume_thread` (which calls setMessages with the thread steps).
    // If we send as soon as currentThreadId matches, our optimistic message and
    // the streamed reply get wiped by the late resume_thread setMessages, and the
    // user only sees them after a refresh. Gating on messages.length > 0 means
    // resume_thread has already applied (clear() emptied it, first_interaction
    // doesn't touch it) — a forked shared thread always carries the shared
    // history + fork divider, so this is reliably non-empty after resume.
    useEffect(() => {
        const pendingMessageStr = sessionStorage.getItem('pendingMessage');
        if (!pendingMessageStr) return;
        if (!connected || !currentThreadId || messages.length === 0) return;
        if (processedPendingRef.current === currentThreadId) return;

        try {
            const pendingMessage = JSON.parse(pendingMessageStr);
            if (pendingMessage.threadId !== currentThreadId) return;

            if (pendingMessage.value?.trim()) {
                processedPendingRef.current = currentThreadId;
                sessionStorage.removeItem('pendingMessage');
                onSubmit(
                    pendingMessage.value,
                    pendingMessage.attachments || [],
                    pendingMessage.commandId
                );
            }
        } catch (err) {
            sessionStorage.removeItem('pendingMessage');
        }
    }, [connected, currentThreadId, messages.length, onSubmit]);

    useEffect(() => {
        if (!pendingPrompt || !connected || messages.length > 0) return;
        setPendingPrompt(null);
        onSubmit(pendingPrompt);
    }, [pendingPrompt, connected, messages.length, onSubmit, setPendingPrompt]);

    useEffect(() => {
        if (!queuedPrompt || !connected || disabled || isTaskRunning) return;
        setQueuedPrompt(null);
        onSubmit(queuedPrompt);
    }, [
        queuedPrompt,
        connected,
        disabled,
        isTaskRunning,
        onSubmit,
        setQueuedPrompt
    ]);

    // Reset processed flag when thread changes
    useEffect(() => {
        return () => {
            processedPendingRef.current = null;
        };
    }, [currentThreadId]);

    const inputWrapper = useRef<HTMLDivElement>(null);

    const handleWrapperClick = useCallback(
        (e: React.MouseEvent<HTMLDivElement>) => {
            const target = e.target as HTMLElement;

            // Focus the input for any other click in the wrapper
            inputRef.current?.focus();
        },
        []
    );

    return (
        <div
            id="message-composer"
            className="relative flex flex-col w-full max-w-full"
        >
            <SessionBudgetDock />
            {notifications.showPrompt && !askUser ? (
                <NotificationPromptDock
                    onEnable={notifications.enable}
                    onDismiss={notifications.dismiss}
                />
            ) : null}
            {askUser && pendingQuestions ? (
                <AskUserQuestionsDock
                    key={pendingQuestions.id}
                    questions={(pendingQuestions as any).props?.questions ?? []}
                    onComplete={(answers) =>
                        askUser.callback({ answers, submitted: true } as any)
                    }
                    onDismiss={() => askUser.callback({ submitted: false } as any)}
                />
            ) : null}
            {askUser && pendingToolApproval ? (
                <ToolApprovalDock
                    key={pendingToolApproval.id}
                    toolLabel={(pendingToolApproval as any).props?.toolLabel ?? ''}
                    onDecide={(approved) =>
                        askUser.callback({ approved } as any)
                    }
                />
            ) : null}
            <div
                ref={inputWrapper}
                className={cn(
                    'relative flex flex-col bg-white dark:bg-sidebar-accent composer-shadow border w-full max-w-full overflow-hidden cursor-text',
                    isEmbeddedSidebar
                        ? 'pt-5 pb-4 px-5 rounded-lg'
                        : 'p-4 px-4 rounded-xl'
                )}
                onClick={handleWrapperClick}
            >
                {attachments.length > 0 ? (
                    <div className={isEmbeddedSidebar ? 'mb-2' : 'mb-4'}>
                        <Attachments />
                    </div>
                ) : null}
                {dashboardReferences.length > 0 ? (
                    <div className={isEmbeddedSidebar ? 'mb-2' : 'mb-4'}>
                        <DashboardReferences />
                    </div>
                ) : null}
                <Input
                    ref={inputRef}
                    id="chat-input"
                    autoFocus
                    selectedCommand={selectedCommand}
                    setSelectedCommand={setSelectedCommand}
                    onChange={setValue}
                    onPaste={onPaste}
                    onEnter={submit}
                    onLocalCommand={openAgentDebug}
                    placeholder={
                        isEmbeddedSidebar
                            ? 'Type your message here...'
                            : t('chat.input.placeholder')
                    }
                    className={isEmbeddedSidebar ? 'mb-8' : 'mb-8'}
                />
                {/* Agent debug modal — opened by the `/agent_debug` command or the
                    debug-mode slash-command launcher. */}
                <AgentDebugDialog
                    open={agentDebugOpen}
                    onOpenChange={setAgentDebugOpen}
                    requestToken={agentDebugToken}
                    view={agentDebugView}
                />
                <div className="flex flex-nowrap items-center gap-2 min-w-0">
                    <div className="[&::-webkit-scrollbar]:hidden flex flex-nowrap items-center gap-2 min-w-0 overflow-x-auto overflow-y-hidden scrollbar-none">
                        <MoreConfigBtn
                            disabled={disabled}
                            fileSpec={fileSpec}
                            onFileUploadError={onFileUploadError}
                            onFileUpload={onFileUpload}
                            sourcesOpen={configOpen}
                            onSourcesOpenChange={setConfigOpen}
                            onClose={handleConfigClose}
                        />
                        {/* Debug-mode-only slash-command launcher, placed right
                            next to "+". Surfaces `/agent_debug` (also typeable).
                            Renders nothing unless Debug mode is enabled. */}
                        <AgentDebugCommandButton
                            disabled={disabled}
                            onSelect={openAgentDebug}
                        />
                        {/* MCP "sources" picker shows only when MCP tools are
                            available: the standalone chat, or an embedded chat whose
                            report author enabled MCP tools. Hidden otherwise (a
                            report companion / plain chat without MCP has nothing to
                            pick).
                            Also hidden in Managed Agents mode — a managed
                            agent's tools/MCP servers are configured on the
                            agent itself (Agents admin tab), not per-chat. */}
                        {mcpToolsAvailable && composerMode !== 'multiagent' && (
                            <DMSourcesPopover
                                sources={selectedSources}
                                selectedSources={selectedSources}
                                allSources={dataSources}
                                disabled={disabled}
                                isLoading={loadingSources}
                                error={sourcesError}
                                isSaving={savingSources}
                                onToggleSource={toggleSource}
                                onClose={handleConfigClose}
                            />
                        )}
                        <CommandButton
                            disabled={disabled}
                            selectedCommandId={selectedCommand?.id}
                            onCommandSelect={setSelectedCommand}
                        />
                        {chatSettingsInputs.length > 0 && (
                            <Button pill
                                id="chat-settings-open-modal"
                                disabled={disabled}
                                onClick={() => setChatSettingsOpen(true)}
                               
                                variant="ghost"
                                size="icon"
                            >
                                <Settings
                                   
                                    strokeWidth={1.5}
                                />
                            </Button>
                        )}
                        {/* Connect-MCP button shows whenever MCP tools are available
                            (standalone, or an embedded chat with MCP enabled by the
                            report author). Hidden when the embedded chat has no MCP.
                            Also hidden in Managed Agents mode for the same reason
                            as the sources picker above. */}
                        {mcpToolsAvailable && composerMode !== 'multiagent' && (
                            <McpButton disabled={disabled} />
                        )}
                        {/* <VoiceButton disabled={disabled} /> */}

                        <CommandButtons
                            disabled={disabled}
                            selectedCommandId={selectedCommand?.id}
                            onCommandSelect={setSelectedCommand}
                        />
                    </div>
                    <div
                        className={cn(
                            'flex justify-end items-center gap-2 ml-auto min-w-0',
                            !isEmbeddedSidebar && 'max-w-[72%]'
                        )}
                    >
                        <div className="flex-1 min-w-0">
                            {/* The model is a REPORT setting, like the MCP attachment:
                                the report author pins it in BIG's report settings and
                                the reader cannot change it, so neither selector is
                                offered in an embedded chat. Managed Agents were already
                                impossible there (no way to run one next to the report),
                                which is why composerMode's app-wide 'multiagent' lock is
                                ignored here too. */}
                            {!modelSelectorAvailable ? null : pinnedAgentName ? (
                                <span className="block px-4 font-medium text-sm truncate">
                                    {pinnedAgentName}
                                </span>
                            ) : composerMode === 'multiagent' ? (
                                <MultiAgentSelector />
                            ) : (
                                <ModelSelector />
                            )}
                        </div>
                        <AnimatePresence mode="popLayout" initial={false}>
                            <motion.div
                                key={showVoiceButton ? 'voice' : 'submit'}
                                initial={{ opacity: 0, scale: 0.85, filter: 'blur(2px)' }}
                                animate={{ opacity: 1, scale: 1, filter: 'blur(0px)' }}
                                exit={{
                                    opacity: 0,
                                    scale: 0.85,
                                    filter: 'blur(2px)',
                                    transition: spring.moderate.exit
                                }}
                                transition={spring.moderate}
                            >
                                {showVoiceButton ? (
                                    <VoiceBtn disabled={disabled} />
                                ) : (
                                    <SubmitBtn
                                        onSubmit={submit}
                                        onFocus={handleInputFocus}
                                        disabled={disabled || !hasContent}
                                    />
                                )}
                            </motion.div>
                        </AnimatePresence>
                    </div>
                </div>
            </div>
            {!isReportMode &&
                !hideSuggestedPrompts &&
                !hasMessage(messages) &&
                !urlThreadId &&
                suggestedPrompts.length > 0 && (
                    <div className="flex mt-4">
                        <DMSuggestedPrompts
                            prompts={suggestedPrompts}
                            onSelectPrompt={handleSelectSuggestedPrompt}
                        />
                    </div>
                )}
            {/* Old source tags section — replaced by SourcesPill in the toolbar row
            <div className="flex flex-wrap items-center gap-2 mt-3 px-2 max-w-full min-h-4 text-[11px] select-none">
                <div
                    onClick={openSourcesConfig}
                    className="group flex flex-wrap items-center gap-1.5 cursor-pointer"
                    role="button"
                    tabIndex={0}
                    onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                            e.preventDefault();
                            openSourcesConfig();
                        }
                    }}
                    aria-label="Click to configure data sources"
                >
                    {selectedSources && selectedSources.length > 0 ? (
                        selectedSources.map((source, index) => (
                            <span
                                key={source.name}
                                className={`group/tag inline-flex items-center gap-1.5 shadow-xs py-1 pr-2 hover:pr-3 pl-3 border border-border hover:border-border dark:border-border dark:hover:border-border rounded-full font-medium text-foreground dark:text-foreground text-xs bg-background dark:bg-background transition-all duration-500 ease-out ${
                                    mounted
                                        ? 'opacity-100 translate-y-0'
                                        : 'opacity-0 translate-y-3'
                                }`}
                                style={{ transitionDelay: `${index * 100}ms` }}
                            >
                                <div>
                                    {getConfigTypeIcon(
                                        source.configType,
                                        'w-4 h-4'
                                    )}
                                </div>
                                <span className="max-w-[220px] truncate">
                                    {source.displayName}
                                </span>
                                <button
                                    onClick={(e) =>
                                        handleRemoveSource(source.name, e)
                                    }
                                    className="opacity-0 group-hover/tag:opacity-100 group-hover/tag:-mr-1 p-0.5 rounded-full w-0 group-hover/tag:w-4 transition-all duration-200"
                                    aria-label={`Remove ${source.displayName}`}
                                    type="button"
                                >
                                    <X className="shrink-0 w-3 h-3" />
                                </button>
                            </span>
                        ))
                    ) : (
                        <span
                            className={`text-muted-foreground hover:text-muted-foreground dark:hover:text-muted-foreground dark:text-muted-foreground italic transition-all duration-500 ease-out ${
                                mounted
                                    ? 'opacity-100 translate-y-0'
                                    : 'opacity-0 translate-y-3'
                            }`}
                        >
                            No sources selected
                        </span>
                    )}
                </div>
            </div>
            */}
        </div>
    );
}
